import { Box, Flex, Spinner } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { KText } from '@/ui-library/components';
import { ApprovalFlowSteps } from '@/components/ats/ApprovalFlowSteps/ApprovalFlowSteps';
import { useShowToast } from '@/hooks';
import {
  useGetApprovalsByEntityQuery,
  // useValidatePreapprovalMutation,
} from '../api';
import type {
  OfferApprovalFormValues,
  OfferApprovalRef,
  OfferApprovalProps,
} from './offer-approvals/OfferApproval.interface';
import { ScorecardTableExtension } from './offer-approvals/ScorecardTableExtension';
import { AttachFilesPicker } from './offer-approvals/AttachFilesPicker';
import { SelectedAttachmentChips } from './offer-approvals/SelectedAttachmentChips';
import { useOfferAttachments } from './offer-approvals/useOfferAttachments';
import { type SelectionRef } from './offer-approvals/attachFilesApi';
import { approvalAttachmentErrorCopy } from './offer-approvals/attachFilesCopy';
import { useOfferStore } from '../store';

export const OfferApproval = forwardRef<OfferApprovalRef, OfferApprovalProps>(
  (props, comRef) => {
    const offerId = useOfferStore((state) => state.offerId);
    const {
      entityId = offerId,
      entityType = 'offer',
      currentOffer = {},
    } = props;
    const formRef = useRef<any>(null);
    const editorInstanceRef = useRef<any>(null);

    const { data: approvalData, isLoading: isApprovalLoading } =
      useGetApprovalsByEntityQuery({
        entityId,
        entityType,
      });

    // Grouped attachment picker. Only offer documents are pre-checked (amendment_12);
    // resume and scorecards are an explicit choice. Selection is a set of references,
    // persisted via seam_2 on "Add" / chip removal — not bundled into the note payload
    // below.
    const attachments = useOfferAttachments(offerId);
    const toast = useShowToast();

    // The picker's "Add" path already toasts on a seam_2 failure
    // (AttachFilesPicker's handleAdd) — the chip ✕ reaches the exact same
    // mutation via removeConfirmed and was previously wired to it directly,
    // leaving a rejected persist an unhandled promise with no user-visible
    // feedback at all.
    const handleRemoveAttachment = async (attachment: SelectionRef) => {
      try {
        await attachments.removeConfirmed(attachment);
      } catch (error) {
        toast({
          title: 'Failed to remove attachment',
          description: approvalAttachmentErrorCopy(error),
          status: 'error',
        });
      }
    };

    useImperativeHandle(comRef, () => ({
      getModel: async () => {
        const formValues = formRef.current?.values as OfferApprovalFormValues;
        const errors = await formRef.current?.validateForm();

        const hasValidData = !errors || Object.keys(errors).length === 0;

        // Get editor instance and convert content to HTML
        const editor = formValues?.editorInstance || editorInstanceRef.current;
        const notesHtml = editor?.getHTML?.() || '';
        return {
          hasValidData,
          payload: {
            note: notesHtml,
          },
        };
      },
    }));

    const initialValues: OfferApprovalFormValues = useMemo(
      () => ({
        note: currentOffer?.approval_request_note || '',
      }),
      [currentOffer]
    );

    // CORE-5264 Decision 3: the manual "+ Scorecard history" insert button is
    // gone — the "All previous scorecards" attachment replaces it (keeping both
    // would show the approver the same scorecards twice).
    // ScorecardTableExtension itself STAYS registered below: its parseHTML
    // still renders scorecard tables saved in approval_request_note on offers
    // already in flight (G1/G3). Only the insertion path is removed.
    const formConfig = useMemo(
      () => [
        {
          name: 'note',
          type: 'editor',
          isRequired: false,
          placeholder: 'Add a note for the approvers...',
          editorStyles: {
            minHeight: '150px',
          },
          editorContainerStyles: {
            border: 'none',
          },
          pluginsConfig: ['bold', 'italic', 'strike', 'image', 'link'],
          customExtensions: [ScorecardTableExtension],
          hideToolbar: false,
          getEditorInstance: true,
        },
      ],
      []
    );

    const renderApproverNote = () => {
      return (
        <Box border="1px solid" borderColor="border-color" borderRadius="md">
          <Flex
            p="16px 20px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="secondary-25"
            justifyContent="space-between"
            alignItems="center"
          >
            <KText size="sm-medium" color="secondary-900">
              Note to approvers
            </KText>
            <AttachFilesPicker offerId={offerId} attachments={attachments} />
          </Flex>
          <FormBuilder
            formRef={formRef}
            config={formConfig}
            initialValues={initialValues}
          />
          <SelectedAttachmentChips
            attachments={attachments.selectedAttachments}
            onRemove={handleRemoveAttachment}
          />
        </Box>
      );
    };

    const renderApproverFlow = () => {
      return (
        <Box border="1px solid" borderColor="border-color" borderRadius="md">
          <KText
            p="16px 20px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="secondary-25"
            size="sm-medium"
            color="secondary-900"
          >
            Offer approval flow
          </KText>
          <ApprovalFlowSteps
            config={approvalData || {}}
            isLoading={isApprovalLoading}
            onClose={() => {}}
            entityType={entityType}
            entityId={entityId}
            onSuccess={() => {}}
            renderModalInParent={false}
            viewMode={true}
            offerId={offerId}
            handleModalOpen={undefined}
            handleUserSelect={undefined}
          />
        </Box>
      );
    };

    if (isApprovalLoading) {
      return (
        <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
          <Spinner color="primary-600" />
        </Flex>
      );
    }

    return (
      <Flex gap="12px" flexDir="column">
        {renderApproverNote()}
        {renderApproverFlow()}
      </Flex>
    );
  }
);

OfferApproval.displayName = 'OfferApproval';
